import {
  AppShell,
  Badge,
  Button,
  Center,
  Divider,
  Group,
  Image,
  Indicator,
  Loader,
  NavLink,
  ScrollArea,
  Stack,
  Text,
} from '@mantine/core';
import {
  NavLink as RouterNavLink,
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from 'react-router';
import type { MyAuthorization, TenantOverview } from '@isg/shared-types';
import { islerimSayaci, useIslerim, useMyAuthorization, useSahaGunu } from '../api/queries';
import { ISLERIM_ETIKET, ISLERIM_IKONU, SAPKA_ETIKET } from '../i18n/tr';
import { AksiyonlarScreen, AksiyonYeniScreen } from '../screens/AksiyonlarScreen';
import { AksiyonDetayScreen } from '../screens/AksiyonDetayScreen';
import { CalismaSaatiScreen } from '../screens/CalismaSaatiScreen';
import { EgitimDetayScreen } from '../screens/EgitimDetayScreen';
import { EgitimKayitScreen } from '../screens/EgitimKayitScreen';
import { EgitimlerScreen } from '../screens/EgitimlerScreen';
import { KanaatDetayScreen } from '../screens/KanaatDetayScreen';
import { KanaatKayitScreen } from '../screens/KanaatKayitScreen';
import { SaglikScreen } from '../screens/SaglikScreen';
import { RiskDegerlendirmeleriScreen } from '../screens/RiskDegerlendirmeleriScreen';
import { RiskDetayScreen } from '../screens/RiskDetayScreen';
import { OlayDetayScreen } from '../screens/OlayDetayScreen';
import { OlayKayitScreen } from '../screens/OlayKayitScreen';
import { OlaylarScreen } from '../screens/OlaylarScreen';
import { IslerimScreen } from '../screens/IslerimScreen';
import { CompaniesScreen } from '../screens/CompaniesScreen';
import { YuklenicilerScreen } from '../screens/YuklenicilerScreen';
import { YukleniciEvrakPaketleriScreen } from '../screens/YukleniciEvrakPaketleriScreen';
import { YukleniciDetayScreen } from '../screens/YukleniciDetayScreen';
import { EquipmentScreen } from '../screens/EquipmentScreen';
import { EkipmanDetayScreen } from '../screens/EkipmanDetayScreen';
import { GorevDetayScreen } from '../screens/GorevDetayScreen';
import { GorevTanimlariScreen } from '../screens/GorevTanimlariScreen';
// Tur 4½ / Aşama B — görevlendirme yüzeyleri (§25/8, B3 + B4).
import { GorevlendirmelerScreen } from '../screens/GorevlendirmelerScreen';
import { KkdZimmetleriScreen } from '../screens/KkdZimmetleriScreen';
import { SozlukYonetimiScreen } from '../screens/SozlukYonetimiScreen';
import { GorevlerScreen } from '../screens/GorevlerScreen';
import { YukumlulukKutuphaneScreen } from '../screens/YukumlulukKutuphaneScreen';
import { YukumlulukScreen } from '../screens/YukumlulukScreen';
import { FormDoldurScreen } from '../screens/FormDoldurScreen';
import { FormKayitScreen } from '../screens/FormKayitScreen';
import { FormKayitlariScreen, FormlarScreen } from '../screens/FormlarScreen';
import { SablonDuzenleScreen } from '../screens/SablonDuzenleScreen';
import { KullanicilarScreen } from '../screens/KullanicilarScreen';
import { YapilandirmaScreen } from '../screens/YapilandirmaScreen';
import { OlaySeviyeScreen } from '../screens/OlaySeviyeScreen';
import { PtwYapilandirmaScreen } from '../screens/PtwYapilandirmaScreen';
import { HomeScreen } from '../screens/HomeScreen';
import { IzinDetayScreen } from '../screens/IzinDetayScreen';
import { IzinTalepScreen } from '../screens/IzinTalepScreen';
import { IzinQrScreen } from '../screens/IzinQrScreen';
import { IzinSahaScreen } from '../screens/IzinSahaScreen';
import { IzinlerScreen } from '../screens/IzinlerScreen';
import { KvkkConsentScreen } from '../screens/KvkkConsentScreen';
import { OnayZincirleriScreen } from '../screens/OnayZincirleriScreen';
import { IsyeriDetayScreen } from '../screens/IsyeriDetayScreen';
import { KisiEkleScreen } from '../screens/KisiEkleScreen';
import { OrgTreeScreen } from '../screens/OrgTreeScreen';
import { PersonCardScreen } from '../screens/PersonCardScreen';
import { PersonsScreen } from '../screens/PersonsScreen';
import { SahaScreen } from '../screens/SahaScreen';
import { UygunsuzlukDetayScreen } from '../screens/UygunsuzlukDetayScreen';
import { UygunsuzluklarScreen } from '../screens/UygunsuzluklarScreen';
import { SahaTurlariScreen } from '../screens/SahaTurlariScreen';
import { SahaTuruDetayScreen } from '../screens/SahaTuruDetayScreen';
import { SahaTuruYeniScreen } from '../screens/SahaTuruYeniScreen';
import { KurulScreen } from '../screens/KurulScreen';
import { KurulYeniScreen } from '../screens/KurulYeniScreen';
import { KurulDetayScreen } from '../screens/KurulDetayScreen';
import { KurulToplantiYeniScreen } from '../screens/KurulToplantiYeniScreen';
import { KurulToplantiDetayScreen } from '../screens/KurulToplantiDetayScreen';
import { AcilDurumScreen } from '../screens/AcilDurumScreen';
import { AcilDurumIsyeriScreen } from '../screens/AcilDurumIsyeriScreen';
import { DokumanlarScreen } from '../screens/DokumanlarScreen';
import {
  BANT_IC_PAY,
  BANT_LOGO_SOL_KAYMA_PX,
  BANT_LOGO_YUKSEKLIK_PX,
  ANLAM,
  BANT_YUKSEKLIK_PX,
  KABUK_KIRILMA,
  MARKA,
  MENU_DIS_PAY,
  MENU_GENISLIK_PX,
  MENU_SATIR_IC_PAY,
} from '../theme';
import {
  CekmeceAlti,
  CekmeceBaglami,
  MenuBurger,
  useKabukOlcusu,
  useMenuCekmecesi,
} from './dar-ekran';
import { GENEL_BAKIS, aktifGrupAnahtari, menuGruplari } from './menu';
import { useMenuGrupDurumu } from './menu-durumu';
import { ModulSayfasi } from './ModulSayfasi';
import type { Sekme } from './ModulSayfasi';
import { PortalShellLayout } from './PortalShellLayout';
import { ZilMenusu } from './ZilMenusu';

/**
 * Uygulama kabuğu (F1·S10; **menü rafı F4·S5·B1 — Kapsam §20.3a**).
 *
 * MENÜ GÖRÜNÜRLÜĞÜ ŞAPKAYA GÖRE: /api/my-authorization'dan gelen birleşim
 * kümesi ucun okuyabildiği ekranları açar. Bu bir yetki KARARI değildir —
 * karar her istekte API guard'ında; menü yalnız 403 alacak ekranı baştan
 * göstermez. Ekranlar API'nin bahçe filtresine GÜVENİR, ikinci filtre yazmaz.
 *
 * ✏️ **F4·S5·B1 — RAF KURULDU (22 satır → 13):** menü modeli artık ayrı bir
 * saf modüldedir (`menu.ts`: üç başlıklı grup · TR harmanlama · boş grubun
 * başlığını da gizleme) ve kapanan satırlar modüllerinin İÇİNE sekme olarak
 * girdi (`ModulSayfasi`). Eski rotalar kırılmadı — hepsi yeni adreslerine
 * yönlendirilir.
 *
 * ⚠️ **ÜST BARDAKİ "İŞLERİM" SAYACI, DASHBOARD BLOĞUYLA AYNI YÜKLEMDEN
 * OKUR** (`useIslerim` — IA §3/2). İkinci bir sayım yazma.
 */

/** Modül içi sekmeler (IA §4.1). Görünürlüğü SUNUCU söyler (IA §3/4). */
function izinSekmeleri(authz: MyAuthorization): Sekme[] {
  return [
    { key: 'izinler-liste', path: '/izinler' },
    { key: 'izinler-saha', path: '/izinler/saha' },
    {
      key: 'izinler-zincirler',
      path: '/izinler/onay-zincirleri',
      gorunur: authz.yuzeyler.ptwYapilandirma,
    },
    {
      key: 'izinler-yapilandirma',
      path: '/izinler/yapilandirma',
      gorunur: authz.yuzeyler.ptwYapilandirma,
    },
  ];
}

function olaySekmeleri(authz: MyAuthorization): Sekme[] {
  return [
    { key: 'olaylar-liste', path: '/olaylar' },
    // Çalışma saatleri sekmesi GATE'SİZDİR ve bu bilinçli: `GET work-hours`
    // tüm iç kullanıcılara + izleyiciye açıktır (kaza oranının paydası).
    { key: 'olaylar-calisma-saatleri', path: '/olaylar/calisma-saatleri' },
    {
      key: 'olaylar-siddet-seviyesi',
      path: '/olaylar/siddet-seviyesi',
      gorunur: authz.yuzeyler.olayYapilandirma,
    },
  ];
}

/**
 * ✏️ **TUR 8 / C2 — YASAL UYGUNLUK sekmeleri.** İkisi de AYNI okuma
 * kümesindedir (`YUKUMLULUK_OKUMA_SAPKALARI`) → `gorunur` alanı YOK; kütüphane
 * bir **yapılandırma sekmesi değildir** (yazma yetkisi ayrı, okuma aynı).
 * ⚠️ Yazabilme kararı SUNUCUDADIR; sekme yalnız görünümü taşır (§2.16).
 */
const YUKUMLULUK_SEKMELERI: Sekme[] = [
  { key: 'yasal-uygunluk-takvim', path: '/yasal-uygunluk' },
  { key: 'yasal-uygunluk-kutuphane', path: '/yasal-uygunluk/kutuphane' },
];

const ORGANIZASYON_SEKMELERI: Sekme[] = [
  { key: 'organizasyon-sirketler', path: '/organizasyon' },
  { key: 'organizasyon-agac', path: '/organizasyon/agac' },
];

/**
 * İŞ EKİPMANLARI sekmeleri (F4·S5·B2 — T8): periyodik görev listesi
 * **nesnesinin modülünde** durur; menüde satır artmaz. Üçü de aynı okuma
 * kümesindedir (T10/#1) → `gorunur` alanı YOK; yapılandırma sekmesi değildir.
 */
/**
 * ÇALIŞANLAR sekmeleri (Tur 4½ / B3): görevlendirme listesi **nesnesinin
 * modülünde** durur (T8 deseni — ekipman görevlerinin aynısı); menüde satır
 * ARTMAZ ve bağlam yerinde kalır.
 *
 * ✏️ MADDE 287 (29.09.2026): ~~"İkisi de aynı okuma kümesindedir"~~ — tur 9
 * çalışan okumasını tüm iç şapkalara açtı, görevlendirme okumasını açmadı;
 * sekme altı şapkada çiziliyor, ana ucu 403 veriyordu. Görünürlük artık
 * SUNUCUDAN (`yuzeyler.gorevlendirmeListesi` — ucun kendi sabiti).
 */
function calisanSekmeleri(authz: MyAuthorization | undefined): Sekme[] {
  return [
    { key: 'calisanlar-liste', path: '/calisanlar' },
    {
      key: 'calisanlar-gorevlendirmeler',
      path: '/calisanlar/gorevlendirmeler',
      // Bağlam yoksa sekme çizilmez (fail-closed).
      gorunur: authz?.yuzeyler.gorevlendirmeListesi ?? false,
    },
    // KKD (Kapsam §30/8): zimmet listesi de nesnesinin modülünde — üçüncü sekme, menüde satır artmaz.
    { key: 'calisanlar-kkd-zimmetleri', path: '/calisanlar/kkd-zimmetleri' },
  ];
}

/**
 * ✏️ 5½ / A3: SÖZLÜK SEKMELERİ KALKTI — dört özel sekme (Diğer kuyruğu ·
 * Görevlendirme türleri · Eğitim türleri · Eğitim konuları/Muayene türleri)
 * TEK genel ekrana katlandı (`SozlukYonetimiScreen`; Kapsam §19/16). Eski
 * adresler aşağıda yönlendirir.
 */

// D2-1 (17.09.2026 — bulgu 92): şablon tasarlamak ≠ form doldurmak.
const FORM_SEKMELERI: Sekme[] = [
  { key: 'formlar-sablonlar', path: '/formlar' },
  { key: 'formlar-kayitlar', path: '/formlar/kayitlar' },
];

/**
 * ✏️ MADDE 287 (29.09.2026): ~~"Üçü de aynı okuma kümesindedir"~~ — ekipman
 * okuma listesi alan sorumlusunu tanır (madde 228), görev okuma listesi
 * tanımaz (*görev LİSTESİ alan sorumlusuna açılmadı* — S4-5). İki görev
 * sekmesinin görünürlüğü SUNUCUDAN (`yuzeyler.gorevListesi`).
 */
function ekipmanSekmeleri(authz: MyAuthorization | undefined): Sekme[] {
  return [
    { key: 'ekipman-liste', path: '/ekipman' },
    {
      key: 'ekipman-gorevler',
      path: '/ekipman/gorevler',
      gorunur: authz?.yuzeyler.gorevListesi ?? false,
    },
    {
      key: 'ekipman-gorev-tanimlari',
      path: '/ekipman/gorev-tanimlari',
      gorunur: authz?.yuzeyler.gorevListesi ?? false,
    },
  ];
}

// ✏️ TUR 2½a: *Talepler* sekmesi KALKTI (Kapsam §24.1/2 — U2). Talep kavramı
// öldü; göçen kayıtlar Uygunsuzluklar ekranında yaşar ve o, Aksiyonlar'ın
// sekmesi DEĞİL kendi menü satırıdır (kendi nesnesi olduğu için).
// ⚠️ Tek sekmeli şerit çizilmez — modül tek sayfaya indi.
const AKSIYON_SEKMELERI: Sekme[] = [{ key: 'aksiyonlar-liste', path: '/aksiyonlar' }];

export function AppShellLayout({
  tenant,
  onLogout,
}: {
  tenant: TenantOverview;
  onLogout: () => void;
}): React.JSX.Element {
  const authz = useMyAuthorization();
  // Madde 302: saha günü kabukta ÖN YÜKLENİR — formlar açıldığında varsayılan
  // "bugün" önbellekten anında gelir (tek kaynak `useSahaGunu`).
  useSahaGunu();
  // ⚠️ HOOK'LAR ERKEN DÖNÜŞLERDEN ÖNCE: aşağıdaki üç dal (yükleniyor · KVKK ·
  // portal) koşulludur; hook'lar onların altına inerse React kuralı kırılır.
  const olcu = useKabukOlcusu();
  const cekmece = useMenuCekmecesi(olcu.darEkran);
  // Menüde açık grup (29.09.2026 — akordeon: aynı anda TEK grup açık): aktif
  // sayfanın grubu açık başlar, açık grup tarayıcıda hatırlanır. Dar ekran
  // çekmecesi AYNI durumu okur — çekmece ayrı bir menü değil, aynı menünün
  // başka bir yerleşimidir.
  const aktifGrup = aktifGrupAnahtari(useLocation().pathname);
  const grupDurumu = useMenuGrupDurumu(aktifGrup);

  // Şapka bağlamı gelmeden kabuk kurulmaz (F1.5·S12): temsilciye ana kabuğun
  // bir karelik parlaması bile gösterilmez — dallanma veriyle yapılır.
  if (authz.isPending) {
    return (
      <Center h="100vh">
        <Loader />
      </Center>
    );
  }

  // KVKK kapısı (F1.5·S11): onaysız temsilcide kabuk HİÇ açılmaz — onay
  // ekranı tam sayfa çıkar. Karar sunucudadır (muaf olmayan her istek 403);
  // bu dallanma yalnız kullanıcıyı doğru akışa taşır.
  if (authz.data?.kvkkOnayGerekli) {
    return <KvkkConsentScreen onLogout={onLogout} />;
  }

  // YÜKLENİCİ PORTALI (F1.5·S12 — Kapsam §18.2): temsilci ana uygulamayı
  // GÖRMEZ — ayrı dar kabuk açılır. Görünüm sadeleştirmesidir, yetki kararı
  // her istekte guard'dadır (§2.16).
  // P13-b (madde 225): kabuk kararı SUNUCUDAN (`my-authorization.kabuk`) — kabuk şapka dizgesi yazmaz.
  if (authz.data?.kabuk === 'portal') {
    return <PortalShellLayout tenant={tenant} onLogout={onLogout} />;
  }

  const gruplar = menuGruplari(authz.data?.sapkalar ?? []);

  // ── BANDIN TAŞINABİLİR ÖĞELERİ (§11.1) ──────────────────────────────────
  // ⚠️ Her biri TEK DÜĞÜMDÜR ve dar ekranda banttan çekmeceye **taşınır** —
  // iki yerde çizilip biri gizlenmez. Sebep `dar-ekran.tsx` başlığında:
  // `data-testid` taşıyan düğümün ikinci kopyası e2e'de ayrım gözetmeksizin
  // eşleşir ve `ssoCikisi()` gibi tek kapılı yardımcılar çalışamaz.
  const holdingRozeti = <Badge variant="light">{tenant.ad}</Badge>;
  const rolEtiketi = authz.data && (
    <Text size="sm" c="dimmed" data-testid="sapka-etiketi">
      {SAPKA_ETIKET[authz.data.sapka]}
    </Text>
  );
  const cikisDugmesi = (
    <Button
      data-testid="sso-logout"
      size="xs"
      variant="default"
      onClick={onLogout}
      fullWidth={olcu.rolCekmecede}
    >
      Çıkış yap
    </Button>
  );

  return (
    <AppShell
      header={{ height: BANT_YUKSEKLIK_PX }}
      navbar={{
        width: MENU_GENISLIK_PX,
        breakpoint: KABUK_KIRILMA,
        // §11 — DAR EKRANDA MENÜ ÇEKMECEDİR VE KAPALI AÇILIR. Mekanizma
        // Mantine'ın kendisidir: `collapsed.mobile` kırılma noktasının altında
        // çubuğu `translateX(-100%)` ile dışarı alır. Bu prop verilmediğinde
        // (eski hâl) Mantine yalnız genişliği %100 yapıyor, transform
        // YAZMIYORDU — çubuk tam genişliğe yayılıp içeriği örtüyor ve
        // kapatılamıyordu.
        collapsed: { mobile: !cekmece.acik },
      }}
      padding="md"
    >
      <AppShell.Header px={BANT_IC_PAY}>
        {/* MARKA BANDI (18.09.2026): ürün adı yerine LOGO. Ölçüler theme.ts'te
            (§2.38 — ekranda boyut/renk sabitlenmez). `h="100%"` + Group'un
            varsayılan dikey ortalaması, iki katına çıkan bantta rozetin ve sağ
            kontrollerin ortada durmasını sağlar. */}
        {/* ⚠️ DAR BANTTA BOŞLUK DA ÖLÇEĞİN BİR ALT KADEMESİNE İNER. Aritmetik:
            375px'te iç paylar (32) + Burger (44) + logo (169+4) + zil (48)
            zaten 297px yer kaplar — geriye İşlerim düğmesi VE ÜÇ BOŞLUK için
            78px kalır. Varsayılan boşluklar (12 + 16 + 12 = 40) tek başına o
            payın yarısını yer. Değerler ÖLÇEKTEN gelir (G5 — ekranda px
            yazılmaz), yalnız bir kademe aşağı. */}
        <Group
          h="100%"
          justify="space-between"
          wrap="nowrap"
          gap={olcu.kisaBant ? 'xs' : undefined}
        >
          {/* ✏️ 19.09.2026 — SOL GRUPTA NEFES: bant iki katına çıkınca eski `sm`
              aralığı dar kaldı; logo ile tenant rozeti bitişik duruyordu. Değer
              boşluk ÖLÇEĞİNDEN gelir (G5 — ekranda px yazılmaz). */}
          {/* ⚠️ BURGER KENDİ SARMALINDA, `gap="sm"` ile: sol grubun `lg`si
              logo ile rozet arasındaki nefestir (19.09) ve Burger onu miras
              alsaydı 440px'te bant 8px payla kalırdı (ölçüldü). Masaüstünde
              Burger HİÇ ÇİZİLMEZ — `display:none` değil, DOM'da yok — yani
              dış grubun boşluğu da doğmaz: masaüstü görünümü birebir aynı. */}
          <Group gap={olcu.kisaBant ? 'xs' : 'sm'} wrap="nowrap">
            {olcu.darEkran && <MenuBurger cekmece={cekmece} />}
            <Group gap="lg" wrap="nowrap">
              <Image
                src={MARKA.logo}
                alt={MARKA.urunAdi}
                h={BANT_LOGO_YUKSEKLIK_PX}
                w="auto"
                fit="contain"
                // Sol hiza: logonun sol kenarı menü METNİYLE aynı çizgide. Bandın
                // kendi iç payı sağdaki düğmelerle ortak olduğu için değişmez;
                // farkı yalnız logo kapatır (değer theme.ts'te türetilir).
                ml={BANT_LOGO_SOL_KAYMA_PX}
                data-testid="marka-logosu"
              />
              {/* En düşük öncelikli öğe: `BANT_ROZET_ESIGI` altında çekmeceye. */}
              {!olcu.cokDar && holdingRozeti}
            </Group>
          </Group>
          <Group gap={olcu.kisaBant ? 'xs' : 'sm'} wrap="nowrap">
            {/* İŞLERİM (T1b): menüde satır DEĞİL — üst barda sayaçlı giriş.
                Sayı dashboard bloğuyla AYNI yüklemden gelir (IA §3/2).
                ⚠️ Zil ile birlikte DAR EKRANDA DA BANTTA KALIR (§11.1 önem
                sırası): ikisi de bir SAYI taşır ve sayının görevi bakmadan
                görünmektir — çekmeceye inen bir bildirim sayacı, açılmadıkça
                hiç okunmaz. */}
            <IslerimGirisi daralt={olcu.kisaBant} />
            {!olcu.rolCekmecede && rolEtiketi}
            {/* Zil (F2·S2): kendi in_app teslimatları; izleyicide okundu
                işareti yok (salt-okunur genel kural). */}
            {authz.data && <ZilMenusu salt={authz.data.saltOkunur} />}
            {!olcu.rolCekmecede && cikisDugmesi}
          </Group>
        </Group>
      </AppShell.Header>

      {/* Menü BAŞLIKLI GRUPLARDIR (T4) ve gruplar AÇILIR-KAPANIR DEĞİLDİR;
          görünür satırı kalmayan grubun BAŞLIĞI da çizilmez (T5). Gövde
          kaydırılabilir — alt kalemler viewport dışına düşmesin. */}
      <AppShell.Navbar
        p={MENU_DIS_PAY}
        data-testid="ana-menu"
        ref={cekmece.navbarRef}
        // ⚠️ KAPALI ÇEKMECE EKRANDAN ÇIKAR, DOM'DAN ÇIKMAZ: Mantine onu
        // `translateX(-100%)` ile dışarı alır, yani görünmez ama
        // KLAVYEYLE SEKMELENEBİLİR kalır — kullanıcı odağı kaybeder ve
        // ekranda hiçbir şey olmaz (§2.40'ın "görünmüyor ama çalışıyor"
        // tersi). `inert` odağı da tıklamayı da kapatır.
        inert={olcu.darEkran && !cekmece.acik}
      >
        {/* ⚠️ DAR EKRANDA ÇEKMECE ÜÇ KATMANDIR: üstte banttan inen BAĞLAM
            (holding + rol), ortada kaydırılan menü, altta *Çıkış yap*.
            Çıkış kaydırılan listenin DIŞINDADIR — uzun menüde dibe inmek
            zorunda kalınmaz. */}
        {/* ⚠️ ÇEKMECENİN ÜSTÜ BANDIN EŞİĞİNİ OKUR, MENÜNÜN EŞİĞİNİ DEĞİL:
            `darEkran`a bağlansaydı 820px'te rol etiketi HEM BANTTA HEM
            ÇEKMECEDE çizilirdi — aynı `data-testid`den iki düğüm, yani
            `dar-ekran.tsx` başlığındaki tuzağın ta kendisi. Rozet koşulu
            ayrıca yazılır ama kapsanır (cokDar ⊂ rolCekmecede). */}
        {olcu.rolCekmecede && (
          <CekmeceBaglami>
            {olcu.cokDar && holdingRozeti}
            {rolEtiketi}
          </CekmeceBaglami>
        )}
        <ScrollArea type="auto" style={{ flex: 1, minHeight: 0 }}>
          <NavLink
            component={RouterNavLink}
            to={GENEL_BAKIS.path}
            end
            label="Genel bakış"
            onClick={cekmece.kapat}
            data-testid="menu-genel-bakis"
          />
          {gruplar.map((grup) => {
            const acik = grupDurumu.acikMi(grup.key);
            return (
              <Stack key={grup.key} gap={0} mt="sm" data-testid={`menu-grup-${grup.key}`}>
                {/* AYIRICI GRUBUN ÜSTÜNDE DURUR, başlığın altında DEĞİL (görsel
                    dil §10.1): çizgi bir AYIRICIDIR — başlığı kendi kalemlerinden
                    ayırmak onu ait olduğu gruptan koparır ve bir önceki grubun
                    sonuna yapıştırır. Menünün en tepesinde çizgi YOKTUR (ayıracak
                    bir şey yok); ilk grubunkisi onu "Genel bakış"tan ayırır.
                    Üstündeki boşluk Stack'in `mt`sinden gelir. */}
                <Divider mb="xs" />
                {/* ✏️ 29.09.2026 — BAŞLIK BİR DÜĞMEDİR (görsel dil §13; §10.1'in
                    "başlık etkileşimsizdir" kuralı revize edildi): grubu açar ve
                    kapatır. Menü satırıyla AYNI bileşendir (`NavLink`) — hover ·
                    klavye odağı · 44px dokunma tabanı · köşe yarıçapı kurallarını
                    satırdan miras alır, ikinci bir düğme stili açılmadı. Satırdan
                    AĞIRLIK + RENK + BÜYÜK HARFLE ayrışır, BÜYÜKLÜKLE değil.
                    ⚠️ `<a>` DEĞİL `<button>`: başlık bir adrese gitmez; `href`siz
                    bağlantı klavyeyle odaklanamaz.
                    ⚠️ Başlık ÇEKMECEYİ KAPATMAZ (dar ekran): kullanıcı bir adrese
                    gitmedi, menünün içinde geziniyor.
                    ⚠️ KAPALI GRUBUN SATIRLARI DOM'DA KALIR, GÖRÜNMEZ
                    (`Collapse` varsayılanı) — sekme sırasına da girmez. */}
                <NavLink
                  component="button"
                  type="button"
                  opened={acik}
                  // MG-7 — KAPALI GRUP AKTİF SAYFAYI İÇERİYORSA BAŞLIK VURGULUDUR:
                  // aktif satırın işaretlerini (dolgu + sol çubuk) başlık taşır.
                  // Satır gizlidir, yani ekranda yine TEK *"buradasın"* vardır
                  // (görsel dil §10.1 ✏️). Grup AÇIKKEN vurgu başlıkta DURMAZ —
                  // işaret satırın kendisindedir; ikisi birden yanarsa iki
                  // *"buradasın"* olur. Stil ayrıca yazılmadı: `data-active`
                  // kenar çubuğunun mevcut aktif satır kuralını okur.
                  active={!acik && grup.key === aktifGrup}
                  onChange={() => {
                    grupDurumu.degistir(grup.key);
                  }}
                  // Alt satırlar GİRİNTİSİZDİR: menü metninin sol hizası bandaki
                  // logonun hizasıdır (`MENU_METIN_SOL_PX`) ve satır grubun
                  // içine girdi diye kaymaz. Başlığın yatay payı da aynı sabitten.
                  childrenOffset={0}
                  px={MENU_SATIR_IC_PAY}
                  aria-expanded={acik}
                  data-testid={`menu-grup-baslik-${grup.key}`}
                  label={
                    <Text
                      component="span"
                      size="xs"
                      fw={700}
                      tt="uppercase"
                      className="isg-menu-grup-baslik"
                    >
                      {grup.baslik}
                    </Text>
                  }
                >
                  {grup.satirlar.map((satir) => (
                    <NavLink
                      key={satir.key}
                      component={RouterNavLink}
                      to={satir.path}
                      label={satir.label}
                      // §11.1 — kalem seçilince çekmece KAPANIR: kullanıcı bir
                      // adrese gitmiştir, menüde kalmaya değil.
                      onClick={cekmece.kapat}
                      data-testid={`menu-${satir.key}`}
                    />
                  ))}
                </NavLink>
              </Stack>
            );
          })}
        </ScrollArea>
        {olcu.rolCekmecede && <CekmeceAlti>{cikisDugmesi}</CekmeceAlti>}
      </AppShell.Navbar>

      <AppShell.Main>
        <Routes>
          <Route path="/" element={<HomeScreen tenant={tenant} />} />
          {authz.data && (
            <>
              {/* İŞLERİM — menüsüz sayfa (T1b); dönüş yolu ekranın kendisinde. */}
              <Route path="/islerim" element={<IslerimScreen />} />
              <Route
                path="/formlar"
                element={
                  <ModulSayfasi
                    testId="form-modul-sekmeleri"
                    sekmeler={FORM_SEKMELERI}
                    aktif="formlar-sablonlar"
                  >
                    <FormlarScreen />
                  </ModulSayfasi>
                }
              />
              <Route
                path="/formlar/kayitlar"
                element={
                  <ModulSayfasi
                    testId="form-modul-sekmeleri"
                    sekmeler={FORM_SEKMELERI}
                    aktif="formlar-kayitlar"
                  >
                    <FormKayitlariScreen />
                  </ModulSayfasi>
                }
              />
              {/* TUR 3 (F7 — madde 143/144): şablon · doldurma · kayıt ARTIK
                  AYRI SAYFA (P1: bağımsız kayıt ayrı sayfa). Statik segment
                  ("yeni") dinamikten ÖNCE durmak zorunda DEĞİL — rota
                  ayrımını ekran yapıyor (`id === 'yeni'`), böylece geri
                  gezinti tek bileşende kalır. */}
              <Route path="/formlar/sablonlar/:id" element={<SablonDuzenleScreen />} />
              <Route path="/formlar/doldur/:sablonId" element={<FormDoldurScreen />} />
              <Route
                path="/formlar/kayitlar/:id"
                element={<FormKayitScreen authz={authz.data} />}
              />
              {/* YASAL UYGUNLUK (F4·S11·B — IA T8).
                  ✏️ **TUR 8 / C2: İKİNCİ SEKME GELDİ — MENÜDE SATIR ARTMADI**
                  (T8 deseni: yüzey nesnesinin modülünde durur). Takvim
                  *"bu işyerinde ne gerekiyor"*, kütüphane *"kural nedir"*
                  sorusunu cevaplar; ikisi aynı modülün iki yüzüdür.
                  ⚠️ Takvimin KENDİ sayfası P5'e tabidir (detayda sekme yok —
                  çıktıya giden şey sayfanın kendisidir); sekme şeridi MODÜL
                  düzeyindedir ve o kuralı delmez. */}
              <Route
                path="/yasal-uygunluk"
                element={
                  <ModulSayfasi
                    testId="yukumluluk-sekmeleri"
                    sekmeler={YUKUMLULUK_SEKMELERI}
                    aktif="yasal-uygunluk-takvim"
                  >
                    <YukumlulukScreen />
                  </ModulSayfasi>
                }
              />
              <Route
                path="/yasal-uygunluk/kutuphane"
                element={
                  <ModulSayfasi
                    testId="yukumluluk-sekmeleri"
                    sekmeler={YUKUMLULUK_SEKMELERI}
                    aktif="yasal-uygunluk-kutuphane"
                  >
                    <YukumlulukKutuphaneScreen />
                  </ModulSayfasi>
                }
              />
              {/* AKSİYONLAR (IA §4.1) — ✏️ Tur 2½a: Talepler sekmesi kalktı. */}
              <Route
                path="/aksiyonlar"
                element={
                  <ModulSayfasi
                    testId="aksiyon-sekmeleri"
                    sekmeler={AKSIYON_SEKMELERI}
                    aktif="aksiyonlar-liste"
                  >
                    <AksiyonlarScreen />
                  </ModulSayfasi>
                }
              />
              {/* UYGUNSUZLUKLAR (Tur 2½a — Kapsam §24.1/11). Sekme şeridi
                  YOKTUR: modül tek listedir ve kaydın kendi sayfası P5'e tabi. */}
              <Route path="/uygunsuzluklar" element={<UygunsuzluklarScreen />} />
              <Route path="/uygunsuzluklar/:id" element={<UygunsuzlukDetayScreen />} />
              {/* Saha turu / denetim (Kapsam §27): statik `yeni` `:id`den ÖNCE. */}
              <Route path="/saha-turlari" element={<SahaTurlariScreen />} />
              <Route path="/saha-turlari/yeni" element={<SahaTuruYeniScreen />} />
              <Route path="/saha-turlari/:id" element={<SahaTuruDetayScreen />} />
              {/* İSG kurulu (Kapsam §28): statik `yeni` ve `toplanti/:id` `:id`den ÖNCE. */}
              <Route path="/isg-kurulu" element={<KurulScreen />} />
              <Route path="/isg-kurulu/yeni" element={<KurulYeniScreen />} />
              <Route path="/isg-kurulu/toplanti/:id" element={<KurulToplantiDetayScreen />} />
              <Route path="/isg-kurulu/:id/toplanti/yeni" element={<KurulToplantiYeniScreen />} />
              <Route path="/isg-kurulu/:id" element={<KurulDetayScreen />} />
              {/* Acil durum (Kapsam §29): işyeri eksenli liste + işyeri sayfası. */}
              <Route path="/acil-durum" element={<AcilDurumScreen />} />
              <Route path="/acil-durum/:workplaceId" element={<AcilDurumIsyeriScreen />} />
              {/* DOKÜMAN RAFI (Kapsam §31/K1): altı sekme ROTA TABANLI — sekme
                  şeridi ekranın kendi içinde (`DokumanlarScreen` → `ModulSayfasi`);
                  `/dokumanlar` ilk sekmeye düşer. */}
              <Route path="/dokumanlar" element={<Navigate to="/dokumanlar/plan" replace />} />
              <Route path="/dokumanlar/:sekme" element={<DokumanlarScreen />} />
              {/* T13 · T4 — YENİ AKSİYON ve AKSİYON DETAYI. Statik segment
                  ("yeni") dinamikten ÖNCE durur: "yeni" bir aksiyon kimliği
                  değildir (izin talep formu rotasıyla aynı gerekçe).
                  ⚠️ İkisi de ModulSayfasi sekme şeridi TAŞIMAZ — sekme MODÜL
                  düzeyindedir, kaydın kendi sayfası P5'e tabidir. */}
              <Route path="/aksiyonlar/yeni" element={<AksiyonYeniScreen />} />
              <Route path="/aksiyonlar/:id" element={<AksiyonDetayScreen />} />
              {/* İŞ İZİNLERİ + Saha · Onay zincirleri · Yapılandırma sekmeleri.
                  Talep formu rotası detaydan ÖNCE durur: "yeni" bir izin
                  kimliği değildir (statik segment dinamikten önce eşleşir). */}
              <Route
                path="/izinler"
                element={
                  <ModulSayfasi
                    testId="izin-modul-sekmeleri"
                    sekmeler={izinSekmeleri(authz.data)}
                    aktif="izinler-liste"
                  >
                    <IzinlerScreen />
                  </ModulSayfasi>
                }
              />
              <Route
                path="/izinler/saha"
                element={
                  <ModulSayfasi
                    testId="izin-modul-sekmeleri"
                    sekmeler={izinSekmeleri(authz.data)}
                    aktif="izinler-saha"
                  >
                    <SahaScreen />
                  </ModulSayfasi>
                }
              />
              <Route
                path="/izinler/onay-zincirleri"
                element={
                  <ModulSayfasi
                    testId="izin-modul-sekmeleri"
                    sekmeler={izinSekmeleri(authz.data)}
                    aktif="izinler-zincirler"
                  >
                    <OnayZincirleriScreen />
                  </ModulSayfasi>
                }
              />
              <Route
                path="/izinler/yapilandirma"
                element={
                  <ModulSayfasi
                    testId="izin-modul-sekmeleri"
                    sekmeler={izinSekmeleri(authz.data)}
                    aktif="izinler-yapilandirma"
                  >
                    <PtwYapilandirmaScreen />
                  </ModulSayfasi>
                }
              />
              <Route path="/izinler/yeni" element={<IzinTalepScreen />} />
              <Route path="/izinler/:permitId" element={<IzinDetayScreen />} />
              <Route path="/izinler/:permitId/saha" element={<IzinSahaScreen />} />
              <Route path="/izinler/:permitId/qr" element={<IzinQrScreen />} />
              {/* OLAYLAR + Çalışma saatleri · Şiddet seviyesi sekmeleri. */}
              <Route
                path="/olaylar"
                element={
                  <ModulSayfasi
                    testId="olay-sekmeleri"
                    sekmeler={olaySekmeleri(authz.data)}
                    aktif="olaylar-liste"
                  >
                    <OlaylarScreen />
                  </ModulSayfasi>
                }
              />
              <Route
                path="/olaylar/calisma-saatleri"
                element={
                  <ModulSayfasi
                    testId="olay-sekmeleri"
                    sekmeler={olaySekmeleri(authz.data)}
                    aktif="olaylar-calisma-saatleri"
                  >
                    <CalismaSaatiScreen />
                  </ModulSayfasi>
                }
              />
              <Route
                path="/olaylar/siddet-seviyesi"
                element={
                  <ModulSayfasi
                    testId="olay-sekmeleri"
                    sekmeler={olaySekmeleri(authz.data)}
                    aktif="olaylar-siddet-seviyesi"
                  >
                    <OlaySeviyeScreen />
                  </ModulSayfasi>
                }
              />
              <Route path="/olaylar/yeni" element={<OlayKayitScreen />} />
              <Route path="/olaylar/:incidentId" element={<OlayDetayScreen />} />
              {/* SÖZLÜK YÖNETİMİ (5½ / A1-A3 — Kapsam §19/16): TEK ekran, solda
                  liste kodları, sağda kalemler. Eski dört özel adres YÖNLENDİRİR;
                  "Diğer kuyruğu"nun olay altındaki eski yolu YÖNLENDİRMEZ, aynı
                  ekranı açar (19.08 kararı — menu.spec bunu ölçer). */}
              <Route path="/sozluk-bakim" element={<SozlukYonetimiScreen />} />
              <Route path="/sozluk-bakim/:listeKodu" element={<SozlukYonetimiScreen />} />
              <Route path="/olaylar/diger-kuyrugu" element={<SozlukYonetimiScreen />} />
              <Route path="/yapilandirma" element={<YapilandirmaScreen />} />
              {/* F4·B2 (Y1) — IA §4'ün 14. satırı: hesap + şapka atama. */}
              <Route path="/kullanicilar" element={<KullanicilarScreen />} />
              <Route path="/saglik" element={<SaglikScreen />} />
              <Route path="/saglik/yeni" element={<KanaatKayitScreen />} />
              <Route path="/saglik/:opinionId" element={<KanaatDetayScreen />} />
              <Route path="/risk" element={<RiskDegerlendirmeleriScreen />} />
              {/* madde 220 / R6 — kayıt modalda AÇILMAZ: boş tablo sayfası,
                  künye üstte. Aynı ekran; kaydedince kaydın adresine geçer. */}
              <Route path="/risk/yeni" element={<RiskDetayScreen />} />
              <Route path="/risk/:assessmentId" element={<RiskDetayScreen />} />
              <Route path="/egitimler" element={<EgitimlerScreen />} />
              <Route path="/egitimler/yeni" element={<EgitimKayitScreen />} />
              <Route path="/egitimler/:trainingId" element={<EgitimDetayScreen />} />
            </>
          )}
          {/* ORGANİZASYON: Şirketler + ağaç görünümü tek satırda (IA §4.1 —
              "Lokasyonlar" ÜÇÜNCÜ satır olarak AÇILMAZ: aynı veri). */}
          <Route
            path="/organizasyon"
            element={
              <ModulSayfasi
                testId="organizasyon-sekmeleri"
                sekmeler={ORGANIZASYON_SEKMELERI}
                aktif="organizasyon-sirketler"
              >
                <CompaniesScreen />
              </ModulSayfasi>
            }
          />
          <Route
            path="/organizasyon/agac"
            element={
              <ModulSayfasi
                testId="organizasyon-sekmeleri"
                sekmeler={ORGANIZASYON_SEKMELERI}
                aktif="organizasyon-agac"
              >
                <OrgTreeScreen />
              </ModulSayfasi>
            }
          />
          {/* ✏️ TUR 9 / B1 — İŞYERİ KARTI (P1: kendi sayfası olabilen kayıt).
              Sekme şeridi TAŞIMAZ: bir kayıt detayıdır, modül sayfası değil
              (§2.32/P5 — detayda sekme yok). */}
          <Route path="/organizasyon/isyeri/:workplaceId" element={<IsyeriDetayScreen />} />
          <Route
            path="/calisanlar"
            element={
              <ModulSayfasi
                testId="calisan-modul-sekmeleri"
                sekmeler={calisanSekmeleri(authz.data)}
                aktif="calisanlar-liste"
              >
                <PersonsScreen />
              </ModulSayfasi>
            }
          />
          {/* ⚠️ Statik segmentler (`yeni` · `gorevlendirmeler`) `:personId`
              kalıbından ÖNCE durur — sonra dursaydı kişi kartı rotası onları
              yutardı. */}
          <Route path="/calisanlar/yeni" element={<KisiEkleScreen />} />
          <Route
            path="/calisanlar/gorevlendirmeler"
            element={
              <ModulSayfasi
                testId="calisan-modul-sekmeleri"
                sekmeler={calisanSekmeleri(authz.data)}
                aktif="calisanlar-gorevlendirmeler"
              >
                <GorevlendirmelerScreen />
              </ModulSayfasi>
            }
          />
          <Route
            path="/calisanlar/kkd-zimmetleri"
            element={
              <ModulSayfasi
                testId="calisan-modul-sekmeleri"
                sekmeler={calisanSekmeleri(authz.data)}
                aktif="calisanlar-kkd-zimmetleri"
              >
                <KkdZimmetleriScreen />
              </ModulSayfasi>
            }
          />
          {/* Kayıt detayı SEKME ŞERİDİ TAŞIMAZ (P5). */}
          <Route path="/calisanlar/:personId" element={<PersonCardScreen />} />
          {/* İŞ EKİPMANLARI + Periyodik görevler · Görev tanımları sekmeleri
              (T8 — görev listesi NESNESİNİN modülünde durur; menüde satır
              artmaz ve bağlam yerinde kalır). Sekmeler ROTA TABANLIDIR;
              görünürlük kararı yok — üçü de aynı okuma kümesindedir. */}
          <Route
            path="/ekipman"
            element={
              <ModulSayfasi
                testId="ekipman-modul-sekmeleri"
                sekmeler={ekipmanSekmeleri(authz.data)}
                aktif="ekipman-liste"
              >
                <EquipmentScreen />
              </ModulSayfasi>
            }
          />
          <Route
            path="/ekipman/gorevler"
            element={
              <ModulSayfasi
                testId="ekipman-modul-sekmeleri"
                sekmeler={ekipmanSekmeleri(authz.data)}
                aktif="ekipman-gorevler"
              >
                <GorevlerScreen />
              </ModulSayfasi>
            }
          />
          <Route
            path="/ekipman/gorev-tanimlari"
            element={
              <ModulSayfasi
                testId="ekipman-modul-sekmeleri"
                sekmeler={ekipmanSekmeleri(authz.data)}
                aktif="ekipman-gorev-tanimlari"
              >
                <GorevTanimlariScreen />
              </ModulSayfasi>
            }
          />
          {/* Detay sayfaları SEKME ŞERİDİ TAŞIMAZ (P5): kayıt detayında
              sekme yoktur; dönüş yolu ekranın kendisindedir (P6). "gorevler"
              statik segmenti `:equipmentId` kalıbından ÖNCE durur. */}
          <Route path="/ekipman/gorevler/:occurrenceId" element={<GorevDetayScreen />} />
          {/* ✏️ TUR 4 ARTÇISI / Y1 — EKİPMANIN KENDİ SAYFASI. Künye · durum ·
              kontrol geçmişi · bağlı görevler · risk maddeleri tek sayfada
              (P5: kayıt detayında sekme yok). Eski iki adres ÖLMEDİ, buraya
              yönlendirilir — yer imleri ve bildirim bağlantıları kırılmaz. */}
          <Route path="/ekipman/:equipmentId" element={<EkipmanDetayScreen />} />
          <Route path="/ekipman/:equipmentId/durum" element={<EkipmanEskiRota />} />
          <Route path="/ekipman/:equipmentId/kontroller" element={<EkipmanEskiRota />} />
          <Route path="/yukleniciler" element={<YuklenicilerScreen />} />
          {/* K-102a — evrak paketleri (menü satırı artmaz; yükleniciler listesinden). */}
          <Route path="/yukleniciler/evrak-paketleri" element={<YukleniciEvrakPaketleriScreen />} />
          <Route path="/yukleniciler/:contractorId" element={<YukleniciDetayScreen />} />

          {/* ── ESKİ ROTALAR (F4·S5·B1) ────────────────────────────────────
              Menü 22 satırdan 13'e inerken hiçbir adres ÖLMEDİ: yer imleri,
              e-postalardaki derin bağlantılar ve bildirim yolları yeni
              adreslerine yönlendirilir. Kaldırmak kırık bağlantı üretir. */}
          <Route path="/sirketler" element={<Navigate to="/organizasyon" replace />} />
          <Route path="/org-agaci" element={<Navigate to="/organizasyon/agac" replace />} />
          <Route path="/kisiler" element={<Navigate to="/calisanlar" replace />} />
          <Route path="/kisiler/:personId" element={<PersonCardScreen />} />
          {/* ✏️ Tur 2½a: eski talep adresleri Uygunsuzluklar'a döner —
              hiçbir adres ÖLMEZ (yer imleri + bildirim yolları). */}
          <Route path="/talepler" element={<Navigate to="/uygunsuzluklar" replace />} />
          {/* 5½ / A3 — tur 4½/5'in dört özel sözlük sekmesi genel ekrana katlandı. */}
          <Route
            path="/sozluk-bakim/gorevlendirme-turleri"
            element={<Navigate to="/sozluk-bakim/gorevlendirme_turu" replace />}
          />
          <Route
            path="/sozluk-bakim/egitim-turleri"
            element={<Navigate to="/sozluk-bakim/egitim_turu" replace />}
          />
          <Route
            path="/sozluk-bakim/egitim-konulari"
            element={<Navigate to="/sozluk-bakim/egitim_konusu" replace />}
          />
          <Route
            path="/sozluk-bakim/muayene-turleri"
            element={<Navigate to="/sozluk-bakim/muayene_turu" replace />}
          />
          <Route path="/aksiyonlar/talepler" element={<Navigate to="/uygunsuzluklar" replace />} />
          <Route path="/saha" element={<Navigate to="/izinler/saha" replace />} />
          <Route
            path="/onay-zincirleri"
            element={<Navigate to="/izinler/onay-zincirleri" replace />}
          />
          <Route
            path="/ptw-yapilandirma"
            element={<Navigate to="/izinler/yapilandirma" replace />}
          />
          <Route
            path="/calisma-saatleri"
            element={<Navigate to="/olaylar/calisma-saatleri" replace />}
          />
          <Route path="/olay-seviye" element={<Navigate to="/olaylar/siddet-seviyesi" replace />} />
          <Route path="/bana-atananlar" element={<Navigate to="/islerim" replace />} />
          <Route path="/bekleyen-onaylar" element={<Navigate to="/islerim" replace />} />
        </Routes>
      </AppShell.Main>
    </AppShell>
  );
}

/**
 * Üst bardaki kişisel kuyruk girişi (T1b). Sayı `useIslerim`den gelir —
 * dashboard bloğuyla **aynı yüklem**; iki ayrı sayım yazılırsa sapma
 * kaçınılmazdır ve kullanıcı ikisine de güvenmez (IA §3/2).
 */
function IslerimGirisi({ daralt }: { daralt: boolean }): React.JSX.Element {
  const islerim = useIslerim();
  // Sayı HER ZAMAN yazılır (sıfır dahil): "boş kuyruk" ile "sayaç yüklenmedi"
  // ekranda ayırt edilebilsin.
  //
  // ⚠️ YÜKLENİRKEN SAYI YAZILMAZ ('…'): iki kuyruktan biri gelmişken yazılan
  // KISMİ sayı, dashboard bloğu henüz hiç çizilmemişken üst barda görünür ve
  // ikisi O AN farklı konuşur (e2e bunu yakaladı). İki gösterim yeri HER DOM
  // ANINDA aynı metni taşır — iki biçimde de aynı `islerimSayaci`den.
  const sayi = islerimSayaci(islerim);

  // ── DAR BANT (§11.1 üçüncü basamak): İKON + SAYI ────────────────────────
  //
  // ⚠️ METİN DÜŞER, SAYI DÜŞMEZ. 436px altında metinli düğme zili bandın sağ
  // payına taşırıyordu (ölçüldü: 375'te 32px). Etiket ERİŞİLEBİLİR ADA
  // taşınır — ekran okuyucu hâlâ "İşlerim" duyar.
  //
  // ⚠️ SAYI NİYE YAN ROZET DEĞİL DE KÖŞE SAYACI: yan rozet, düğmeye kendi
  //    genişliği (18px) ÜSTÜNE bir de bölüm boşluğu ekler ve 375'te bant 8px
  //    taşmaya devam ediyordu (ölçüldü). Köşe sayacı MUTLAK konumludur, yani
  //    yerleşime genişlik eklemez ve düğme zille AYNI ölçüye (48px) iner —
  //    yan yana iki ikon düğmesi, her biri kendi sayısıyla.
  if (daralt) {
    return (
      <Indicator
        label={sayi}
        size={16}
        // ⚠️ ZİLDEN BİLİNÇLİ FARK: zil `disabled={okunmamis === 0}` ile sıfırda
        //    sayacı GİZLER; İşlerim GİZLEMEZ. Sebep yukarıdaki kural — "boş
        //    kuyruk" ile "sayaç yüklenmedi" ayrımı sayının VARLIĞINA bağlıdır.
        color={ANLAM.bilgi}
        data-testid="islerim-sayac"
      >
        <Button
          component={Link}
          to="/islerim"
          size="xs"
          variant="default"
          aria-label={ISLERIM_ETIKET}
          data-testid="islerim-girisi"
        >
          {ISLERIM_IKONU}
        </Button>
      </Indicator>
    );
  }

  return (
    <Button
      component={Link}
      to="/islerim"
      size="xs"
      variant="default"
      data-testid="islerim-girisi"
      rightSection={
        <Badge
          size="sm"
          circle
          variant={islerim.toplam > 0 && !islerim.yukleniyor ? 'filled' : 'light'}
          data-testid="islerim-sayac"
        >
          {sayi}
        </Badge>
      }
    >
      {ISLERIM_ETIKET}
    </Button>
  );
}

/**
 * Y1 — ESKİ EKİPMAN ROTALARI (`/durum` · `/kontroller`) KAYDIN KENDİ
 * SAYFASINA YÖNLENDİRİLİR. İkisi de artık o sayfanın birer BÖLÜMÜDÜR (P5);
 * adresleri kaldırmak yer imlerini ve bildirim bağlantılarını kırardı —
 * menü rafı turunun *"hiçbir adres ölmedi"* kuralının ekipman kolu.
 */
function EkipmanEskiRota(): React.JSX.Element {
  const { equipmentId } = useParams();
  return <Navigate to={`/ekipman/${equipmentId ?? ''}`} replace />;
}
